// What the wall shows, by time of day (Scott, 2026-10-04; mockup "Wall, by
// time of day"). Pure projections over the data the wall already holds —
// useWallData's days and the kids' day models — so no new queries on a
// display that polls all day.
import type { TimelineItem } from '@/types/timeline'
import type { FamilyMember } from '@/types/family'
import type { WallDayData } from '@/hooks/useWallData'
import { routineEarnsTheWall } from '@/lib/routineUtils'
import { boardOwnersOf } from '@/components/wall-v2/wallGantt'
import { titleForMember, HOUSEHOLD_ID } from '@/components/wall-v2/wallEventAttribution'
import { bandForTime, type MemberDayModel, type KidRow, type KidPaperRow } from './kidDayModel'
import type { WallMoment } from './wallMoment'

/** "7:30a", "2:10p", "12p". */
export function wallClock(d: Date): string {
  const h = d.getHours()
  const m = d.getMinutes()
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'a' : 'p'}`
}

export interface WallTodayRow {
  id: string
  kind: TimelineItem['type']
  time: string
  end: string | null
  title: string
  sub: string | null
  /** Family member ids who carry it (never the household row). */
  owners: string[]
  past: boolean
  /** A task whose time has passed but is not done: still the house's to do,
   *  so the lists keep it (Today keeps it on the day, open, the same way). */
  open?: boolean
  /** Starting within the next 45 minutes, or under way. */
  now: boolean
  /** Epoch ms of the start / end (the kiosk's lanes and NEXT slot). */
  startsAt?: number
  endsAt?: number
}

/**
 * Today's timed things in order. A routine that runs most days is the week's
 * shape, not news, and stays off (the board's rule, routineEarnsTheWall); a
 * collection step never stands alone, and neither does a task's step: it
 * folds into its task's row as "3 steps" (2026-10-07 — a task and its three
 * steps were four rows at 8:30). Every timed family task is on it, whoever
 * carries it (Scott, 2026-10-17: "if there's a family task on Today The Day
 * it should also show on the kiosk" — replacing 2026-10-07's rule that kept
 * one adult's own task off). Events stay — they say who is where.
 */
/** On a "rest of today" list: still ahead, or a task not done yet. */
export function stillOnToday(r: WallTodayRow): boolean {
  return !r.past || !!r.open
}

export function wallTodayRows(items: Record<string, TimelineItem[]>, members: FamilyMember[], now: Date): WallTodayRow[] {
  const all = Object.values(items).flat()
  const ids = new Set(all.map((it) => it.id))
  const steps = new Map<string, number>()
  for (const it of all) {
    if (it.isSubtask && it.parentTaskId && ids.has(`task-${it.parentTaskId}`) && !it.completed) {
      steps.set(`task-${it.parentTaskId}`, (steps.get(`task-${it.parentTaskId}`) ?? 0) + 1)
    }
  }
  const seen = new Set<string>()
  const rows: (WallTodayRow & { at: number })[] = []
  for (const it of all) {
    if (!it.startTime || it.allDay || seen.has(it.id)) continue
    if (it.isSubtask && it.parentTaskId && ids.has(`task-${it.parentTaskId}`)) continue
    if (it.type === 'routine' && (it.originalRoutine?.parent_routine_id != null || !routineEarnsTheWall(it.recurrencePattern))) continue
    if (it.completed && it.type !== 'event') continue
    const owners = boardOwnersOf(it, members).filter((id) => id !== HOUSEHOLD_ID)
    seen.add(it.id)
    const stepCount = steps.get(it.id) ?? 0
    const start = new Date(it.startTime)
    const end = it.endTime ? new Date(it.endTime) : null
    const endsAt = (end ?? new Date(start.getTime() + 30 * 60_000)).getTime()
    rows.push({
      at: start.getTime(),
      id: it.id,
      kind: it.type,
      time: wallClock(start),
      end: end && end.getTime() - start.getTime() >= 45 * 60_000 ? wallClock(end) : null,
      title: it.title,
      sub: (it as TimelineItem & { location?: string | null }).location?.split(',')[0]
        ?? (stepCount ? `${stepCount} ${stepCount === 1 ? 'step' : 'steps'}` : null),
      owners,
      past: endsAt <= now.getTime(),
      open: it.type === 'task' && endsAt <= now.getTime(),
      now: start.getTime() <= now.getTime() + 45 * 60_000 && endsAt > now.getTime(),
      startsAt: start.getTime(),
      endsAt,
    })
  }
  return rows.sort((a, b) => a.at - b.at).map(({ at: _at, ...r }) => r)
}

export interface WallAnytimeRow { title: string; owners: string[] }

/**
 * The day's things with no clock: a routine that runs sometime today, and a
 * task due today with no time (Scott, 2026-10-30: "Return picture day order
 * form" sat on Today's "For today" list and never reached the wall, so it was
 * missed). One row per title; a thing two kids each carry names them both.
 */
export function wallAnytimeRows(items: Record<string, TimelineItem[]>, members: FamilyMember[]): WallAnytimeRow[] {
  const all = Object.values(items).flat()
  const ids = new Set(all.map((it) => it.id))
  const out: WallAnytimeRow[] = []
  for (const it of all) {
    if (it.completed || (it.startTime && !it.allDay)) continue
    if (it.type === 'routine') {
      if (it.startTime || it.originalRoutine?.parent_routine_id != null) continue
    } else if (it.type === 'task') {
      if (it.isSubtask && it.parentTaskId && ids.has(`task-${it.parentTaskId}`)) continue
    } else continue
    const owners = boardOwnersOf(it, members).filter((id) => id !== HOUSEHOLD_ID)
    const same = out.find((a) => a.title === it.title)
    if (same) { for (const o of owners) if (!same.owners.includes(o)) same.owners.push(o) }
    else out.push({ title: it.title, owners })
  }
  return out
}

export interface SpecialsDay {
  key: string
  day: string
  isToday: boolean
  isTomorrow: boolean
  cells: { memberId: string; text: string }[]
}

const SPECIALS = /^specials\b/i
const ymd = (d: Date) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`

/** The week's school specials, one row per day that has them, each kid's half. */
export function specialsWeek(days: WallDayData[], kids: FamilyMember[], today: Date): SpecialsDay[] {
  const todayKey = ymd(today)
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)
  const out: SpecialsDay[] = []
  for (const d of days) {
    const ev = Object.values(d.items).flat().find((i) => SPECIALS.test(i.title))
    if (!ev) continue
    out.push({
      key: ymd(d.date),
      day: d.date.toLocaleDateString('en-US', { weekday: 'short' }),
      isToday: ymd(d.date) === todayKey,
      isTomorrow: ymd(d.date) === ymd(tomorrow),
      cells: kids.map((k) => {
        const t = titleForMember(ev.title, k.name)
        return { memberId: k.id, text: t === ev.title ? '' : t }
      }),
    })
  }
  return out
}

export interface WallChecklist { title: string; rows: KidRow[] }

const BAND: Record<WallMoment, 'morning' | 'afternoon' | 'evening'> = {
  morning: 'morning', after: 'afternoon', dinner: 'evening', evening: 'evening',
}
const BAND_TITLE = { morning: 'This morning', afternoon: 'This afternoon', evening: 'Tonight' } as const

/** A kid's list for the part of the day: their routine for it (Out the door,
 *  Bedtime…), else that band's own rows. */
export function checklistFor(model: MemberDayModel, moment: WallMoment): WallChecklist | null {
  const band = BAND[moment]
  const collection = model.collections.find((c) => bandForTime(c.timeOfDay) === band && c.rows.length > 0)
  if (collection) return { title: collection.title, rows: collection.rows }
  const rows = model.bands[band] ?? []
  return rows.length ? { title: BAND_TITLE[band], rows } : null
}

/** Work a child does after school: practice and homework, by name. */
const PRACTICE = /\b(math|maths|reading|read|practice|homework|spelling|study|studying)\b/i

/**
 * A kid's after-school work, once each (Scott, 2026-10-07: "the day's
 * homework and math/reading practice … in the afternoon"): homework due by
 * the next school day (or late), then today's practice routines — the reading
 * target and any routine or collection step named for math, reading,
 * spelling or homework. Every row ticks off where it stands.
 */
export function afterSchoolRows(model: MemberDayModel): KidRow[] {
  const out: KidRow[] = []
  const seen = new Set<string>()
  const add = (r: KidRow) => {
    const key = `${r.entityType}:${r.id}`
    if (seen.has(key)) return
    seen.add(key)
    out.push(r)
  }
  for (const h of model.homework) {
    if (!(h.late || h.due === null || h.due === 'Today' || h.due === 'Tomorrow')) continue
    const when = h.late ? 'late' : h.due === 'Tomorrow' ? 'due tomorrow' : null
    add({ entityType: 'task', id: h.id, title: when ? `${h.title} · ${when}` : h.title, done: false, timeOfDay: null, target: null })
  }
  if (model.reading) add(model.reading)
  const routineRows = [...Object.values(model.bands).flat(), ...model.collections.flatMap((c) => c.rows)]
  for (const r of routineRows) if (PRACTICE.test(r.title)) add(r)
  return out
}

/** School work, by name. No bare "practice": soccer practice is not school
 *  work. GAL is the gifted-and-advanced practice (Puzzle Trail). */
const SCHOOLWORK = /\b(math|maths|reading|read|homework|spelling|study|studying|worksheet|fluency|fundations|puzzle|gal)\b/i

/** A piece of a kid's school work: its row (ticked where it stands) and the
 *  one word that says when ("today", "for tomorrow", "late", "6:45"). */
export interface KidWorkRow { row: KidRow; note: string | null }
/** Homework due later this week: not today's to do, but on the wall so it
 *  gets started ("Unit 4 math test · Thu"). */
export interface KidWorkAhead { id: string; title: string; due: string }
export interface KidSchoolWork { today: KidWorkRow[]; week: KidWorkAhead[] }

const hhmmMinutes = (t: string | null) => {
  const m = t?.match(/^(\d{1,2}):(\d{2})/)
  return m ? Number(m[1]) * 60 + Number(m[2]) : null
}
const clockNote = (t: string | null) => {
  const min = hhmmMinutes(t)
  if (min == null) return null
  const h = Math.floor(min / 60) % 12 || 12
  return `${h}:${String(min % 60).padStart(2, '0')}`
}

/** The home-from-school routine: the collection that starts between 3 and 6. */
export function afterSchoolCollection(model: MemberDayModel) {
  return model.collections.find((c) => { const m = hhmmMinutes(c.timeOfDay); return m != null && m >= 15 * 60 && m < 18 * 60 })
}

/**
 * A kid's school work for the wall (Scott, 2026-10-29: "the homework/GAL
 * work/etc. due to be worked on this day or week"). Today: homework due by
 * the next school day (or late, or undated), the reading target, and the
 * day's practice by name — loose routines (Puzzle Trail at 6:45) and the
 * home-from-school routine's steps; never the morning's "Bag packed:
 * homework folder" or bedtime's "Own reading". This week: open homework due
 * in the seven days after tomorrow.
 */
export function kidSchoolWork(model: MemberDayModel, now: Date): KidSchoolWork {
  const today: KidWorkRow[] = []
  const seen = new Set<string>()
  const add = (row: KidRow, note: string | null) => {
    const key = `${row.entityType}:${row.id}`
    if (seen.has(key)) return
    seen.add(key)
    today.push({ row, note })
  }
  const soon = (h: MemberDayModel['homework'][number]) => h.late || h.due === null || h.due === 'Today' || h.due === 'Tomorrow'
  for (const h of model.homework) {
    if (!soon(h)) continue
    add({ entityType: 'task', id: h.id, title: h.title, done: false, timeOfDay: null, target: null },
      h.late ? 'late' : h.due === 'Today' ? 'today' : h.due === 'Tomorrow' ? 'for tomorrow' : null)
  }
  if (model.reading) add(model.reading, model.reading.target ? `${model.reading.target.amount} min` : null)
  const loose = Object.values(model.bands).flat().filter((r) => r.app || SCHOOLWORK.test(r.title))
  const steps = (afterSchoolCollection(model)?.rows ?? []).filter((r) => SCHOOLWORK.test(r.title))
  for (const r of [...steps, ...loose]) add(r, clockNote(r.timeOfDay))

  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const from = new Date(start); from.setDate(from.getDate() + 2)
  const until = new Date(start); until.setDate(until.getDate() + 8)
  const week = model.homework
    .filter((h) => {
      if (soon(h) || !h.dueOn) return false
      const due = new Date(h.dueOn.getFullYear(), h.dueOn.getMonth(), h.dueOn.getDate())
      return due >= from && due < until
    })
    .map((h) => ({ id: h.id, title: h.title, due: h.due as string }))
  return { today, week }
}

/** Out the door (spec 2026-10-20): a paper a parent has, due today and not
 *  yet in the backpack, joins that kid's bag line — a plain fact, the last
 *  chance before school. */
export function bagWithPapers(needed: string[], papers: KidPaperRow[], todayLabel = 'Today'): string[] {
  return [...needed, ...papers.filter((p) => p.heldBy && !p.done && p.due === todayLabel).map((p) => p.title)]
}
